<template>
  <span class="breadcrumbs-generator" data-test="breadcrumb-links"
    ><slot />
    <template
      v-for="(breadcrumb, index) of breadcrumbs"
      :key="`divider-${index}`"
    >
      <span v-if="index > 0" class="crumb">&rsaquo;</span
      ><template v-if="index < breadcrumbs.length - 1"
        ><pu-link
          :key="`${index}${breadcrumb.label}`"
          :path="breadcrumb.url"
          :query="query"
          :disabled="!breadcrumb.linkable"
          :exclude-type="
            isCityLevel(breadcrumb) ? 'breadcrumbCity' : 'breadcrumb'
          "
          :non-seo="breadcrumb.nonSeo"
          :remove-facet-query="breadcrumb.removeFacetQuery"
          :emit-events="true"
          :debug-breadcrumbs="false"
          :data-hierarchy-count="
            breadcrumb.debugData?.hierarchyCount?.toString()
          "
          :data-linkable="breadcrumb.debugData?.linkable?.toString()"
          :data-non-seo="breadcrumb.debugData?.nonSeo?.toString()"
          :data-hierarchy-path="breadcrumb.debugData?.hierarchyPath"
          :data-breadcrumb-level="breadcrumb.debugData?.level?.toString()"
          @url-generated="(url) => updateRenderedUrl(breadcrumb.label, url)"
          >{{ breadcrumb.label }}</pu-link
        ><span
          v-if="withCounts && !!breadcrumb.count"
          :key="`${index}${breadcrumb.label}count`"
          class="count"
          >({{ breadcrumb.count }})</span
        ></template
      >
      <pu-link
        v-else
        :key="`${index}${breadcrumb.label}`"
        :path="breadcrumb.url"
        :query="query"
        :disabled="allowLastLink ? !breadcrumb.linkable : true"
        :exclude-type="
          isCityLevel(breadcrumb) ? 'breadcrumbCity' : 'breadcrumb'
        "
        :non-seo="breadcrumb.nonSeo"
        :remove-facet-query="breadcrumb.removeFacetQuery"
        :emit-events="true"
        :debug-breadcrumbs="false"
        :data-hierarchy-count="breadcrumb.debugData?.hierarchyCount?.toString()"
        :data-linkable="breadcrumb.debugData?.linkable?.toString()"
        :data-non-seo="breadcrumb.debugData?.nonSeo?.toString()"
        :data-hierarchy-path="breadcrumb.debugData?.hierarchyPath"
        :data-breadcrumb-level="breadcrumb.debugData?.level?.toString()"
        @url-generated="(url) => updateRenderedUrl(breadcrumb.label, url)"
        >{{ breadcrumb.label }}</pu-link
      >
    </template>
  </span>
</template>

<script lang="ts">
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { SimpleBreadcrumbWithCount } from '~/components/search/types'
import { getPriceParamsFromQuery } from '~/utility/getPriceParamsFromQuery'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { useSearchStore } from '~/stores/useSearchStore'

@Component({
  emits: ['update-rendered-url'],
})
class BreadcrumbsGenerator extends Vue {
  @Prop({ required: true })
  readonly breadcrumbs: SimpleBreadcrumbWithCount[]

  @Prop({ default: true, type: Boolean })
  readonly withCounts: boolean

  @Prop({ default: false, type: Boolean })
  readonly allowLastLink: boolean

  $route!: RouteLocationNormalizedLoaded

  updateRenderedUrl(label: string, url: string) {
    this.$emit('update-rendered-url', { label, url })
  }

  getPriceParamsFromQuery(query: Record<string, any>) {
    return getPriceParamsFromQuery(query)
  }

  get query() {
    const searchStore = useSearchStore()
    const priceParams = getPriceParamsFromQuery(this.$route.query)

    // Get facets from search store instead of route query
    const facets = searchStore.searchFilters?.facets || []
    const facetParams = facets.length > 0 ? { facet: facets } : {}

    return { ...priceParams, ...facetParams }
  }

  isCityLevel(breadcrumb: SimpleBreadcrumbWithCount) {
    return breadcrumb.hierarchyLevel === 3
  }
}

export default toNative(BreadcrumbsGenerator)
</script>

<style>
@import url('~/assets/css/breadcrumbs.css');
</style>
